import { useCallback } from 'react'
import { UseFormReturnType } from '@mantine/form/lib/types'

import { useInputWarnings } from '@/components/Event/useInputWarnings'
import { ATTENDEE_ROLE_LABEL, AttendeeRole } from '@/constants'
import { AttendeeFormValues, ReporterFeedback } from '@/types'

// Shared attendee-input wiring used by both DrAttendeeInput and CrAttendeeInput:
// the field-path prefix, role option data, forward-propagating updates, and the
// input-warning props. Layout/validation differences stay in the two components.
export const useAttendeeField = ({
  form,
  witnessIndex,
  index,
  roles,
  attendeeWarnings,
}: {
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
  index: number
  roles: AttendeeRole[]
  attendeeWarnings: Set<keyof AttendeeFormValues>[]
}) => {
  const formWarnings = useInputWarnings(attendeeWarnings)
  const values = form.values?.events_feedbacks[witnessIndex].attendees[index]
  const valuePrefix = `events_feedbacks.${witnessIndex}.attendees.${index}`

  // Attendees can appear across several witnesses; an edit propagates to the same
  // attendee id in this witness and all following ones.
  const updateValueAndPropagateForward = useCallback(
    (field: string, value: any) => {
      const feedbacks = form.values?.events_feedbacks

      feedbacks.forEach((_, idx) => {
        if (idx < witnessIndex) {
          return
        }

        const attendeeIdx = form.values?.events_feedbacks[
          idx
        ].attendees.findIndex(attendee => attendee.id === values?.id)

        if (attendeeIdx !== -1) {
          form.setFieldValue(
            `events_feedbacks.${idx}.attendees.${attendeeIdx}.${field}`,
            value,
          )
        }
      })
    },
    [form, values, witnessIndex],
  )

  const roleData = roles.map(role => ({
    value: role,
    label: ATTENDEE_ROLE_LABEL[role],
  }))

  return {
    values,
    valuePrefix,
    updateValueAndPropagateForward,
    roleData,
    formWarnings,
  }
}
